import { CustomerService } from 'src/app/angularJS/services/customer/customer.service';
import { QueryBuilder } from 'src/app/angularJS/services/query-builder.service';
import { LinkBuilder } from 'src/app/angularJS/services/link-builder.service';
import { UserService } from 'src/app/angularJS/services/user/user.service';
import { ACTToolbarService } from 'src/app/angularJS/services/toolbar.service';
import { UserMessagesService } from 'src/app/angularJS/services/user-messages.service';
import { BreadcrumbService } from 'src/app/angularJS/services/breadcrumbs/breadcrumb.service';
import { PaymentService } from 'src/app/angularJS/services/payments/payments.service';
import { ChargeAccountService } from 'src/app/angularJS/services/charge-account/charge-account.service';

/**
* A Controller using the {@link actora.services.customerService customerService} to handle the retrieval and display of customer records, also allows for customer search and delete actions to be performed.
*/
export class CustomerListController {
  public static $inject = [
    'customerService',
    'QueryBuilder',
    'LinkBuilder',
    'userService',
    'ACTToolbarService',
    'UserMessages',
    'breadcrumbService',
    '$translate',
    '$state',
    'PrimaryAction',
    '$rootScope',
    'paymentService',
    'chargeAccountService',
    '$q'];
  public searchText: string;
  public helpText: string;
  public customers: Array<InterACT.Interfaces.Customers.Customer> = [];
  public anonymousCards = [];
  public chargeAccounts: Array<InterACT.Interfaces.ChargeAccounts.ChargeAccount> = [];
  public canEditCustomers = false;
  public customersPaginator: InterACT.Interfaces.IPaginationData = {
    currentPage: 1,
    perPageCount: 15,
    totalCount: 0
  };
  public chargeAccountsPaginator: InterACT.Interfaces.IPaginationData = {
    currentPage: 1,
    perPageCount: 15,
    totalCount: 0
  };

  private shouldSearchChargeAccounts = false;

  constructor(
    private customerService: CustomerService,
    private queryBuilder: QueryBuilder,
    private linkBuilder: LinkBuilder,
    private userService: UserService,
    private toolbarService: ACTToolbarService,
    private UserMessages: UserMessagesService,
    private breadcrumbService: BreadcrumbService,
    private $translate: ng.translate.ITranslateService,
    private $state: ng.ui.IStateService,
    private PrimaryAction: any,
    private $rootScope,
    private paymentService: PaymentService,
    private chargeAccountService: ChargeAccountService,
    private $q: ng.IQService
  ) {
    this.activate();
  }

  /**
 * A method which is used to search customers and charge accounts
 *
 * @param text The search text used to find a customer or charge account
 */
  public search = (text: string) => {
    this.searchText = text;
    this.anonymousCards = [];
    this.customers = [];
    this.chargeAccounts = [];
    this.customersPaginator.currentPage = 1;
    this.customersPaginator.totalCount = 0;

    if (this.searchText === '') {
      return;
    }

    if (this.shouldSearchChargeAccounts) {
      this.searchForChargeAccounts(this.customersPaginator.currentPage, this.customersPaginator.perPageCount);
    }

    this.searchForCustomers(this.customersPaginator.currentPage, this.customersPaginator.perPageCount);
  }

  /**
   * A method that returns the name of the customer that the supplied charge account belongs to
   * @param chargeAccount the charge account to get the customer name for
   */
  public getCustomerName = (chargeAccount: InterACT.Interfaces.ChargeAccounts.ChargeAccount) => {
    if (!chargeAccount.customer) {
      return this.$translate.instant('ANONYMOUS');
    }

    return chargeAccount.customer.name.replace(/\w\S*/g, function (txt) { return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(); });
  };

  /**
   * A method that returns the account name of the charge account
   * @param chargeAccount the charge account to get the account name for
   */
  public getAccountName = (chargeAccount: InterACT.Interfaces.ChargeAccounts.ChargeAccount) => {
    if (!chargeAccount.friendlyName) {
      return "";
    }
    return chargeAccount.friendlyName;
  };

  /**
   * Function to delete a record from the Customer backend service
   *
   * @param customer The customer model representing the record on the server to be deleted.
   *
   * @returns Returns a promise from the buildConfirmDialog function
   */
  public deleteCustomer = (customer: InterACT.Interfaces.Customers.Customer) => {
    return this.UserMessages.buildConfirmDialog(
      'CONFIRM_DIALOG.CUSTOMER.DELETE_THIS',
      'CONFIRM_DIALOG.CUSTOMER.DELETE',
      { customer: this.$translate.instant('CUSTOMER_DETAIL.' + customer.title) + ' ' + customer.forename + ' ' + customer.surname },
      'CONFIRM_DIALOG.CUSTOMER.DELETE_CONFIRM'
    ).then(() => {
      return this.customerService.remove(customer.id).then(() => {
        this.UserMessages.showToast('TOAST_MESSAGE.CUSTOMER.DELETED');
      });
    });
  }

  /**
   * Function to perform a state change via the routing module to the customer.edit state.
   *
   * @param model The customer object representing the record on the server to be deleted.
   */
  public goToEdit = (model) => {
    this.$state.go('customer.edit', { customer: model, customerId: model.id });
  }

  /**
   * Function to take the user to the create screen
   */
  public goToCreate = () => {
    this.$state.go('customers-create');
  }

  /**
   * A method to retrieve the card number
   *
   * @param cardNumber the object for card number
   * @returns string of the card number
   */
  public getCardNumber(cardNumber: InterACT.Interfaces.IDictionary<string>) {
    let _cardNumber = cardNumber['ISRN'];

    if (!_cardNumber) {
      _cardNumber = cardNumber['SERIAL_NUMBER'];
    }

    if (!_cardNumber) {
      return cardNumber[Object.keys(cardNumber)[0]];
    }

    return _cardNumber;
  }

  /**
   * A method to check whether card numbers are present in an object.
   * @param card card object to check
   */
  public cardNumberPresent(card: InterACT.Interfaces.Cards.Card) {
    return card.cardNumber && Object.keys(card.cardNumber).length > 0;
  }

  /**
   * A method that handles the event where a charge account has been selected/clicked in the UI
   * @param chargeAccount the charge account that was selected/clicked
   */
  public onChargeAccountSelected = (chargeAccount: InterACT.Interfaces.ChargeAccounts.ChargeAccount) => {
    this.$state.go('charge-account-summary', { chargeAccountId: chargeAccount.id });
  };

  /**
   * A method that searches for a page of charge accounts using the search string on this controller
   * @param page the page number to retrieve
   * @param perPage the number of items per page
   */
  public searchForChargeAccounts(page: number, perPage: number) {
    const schemeLink = this.linkBuilder.getSchemesLink(this.userService.selectedScheme.id);

    this.getChargeAccountsByCustomerNameOrMediaRef(this.searchText, schemeLink, page, perPage).then(
      (chargeAccountsResponse: InterACT.Interfaces.ChargeAccounts.ChargeAccountsResponse | Array<InterACT.Interfaces.ChargeAccounts.ChargeAccount>) => {
        this.handleGetChargeAccountsbyNameOrMediaRefResponse(<InterACT.Interfaces.ChargeAccounts.ChargeAccountsResponse>chargeAccountsResponse, schemeLink);
      });
  }

  /**
 * A method that searches for a page of customerss using the search string on this controller
 * @param page the page number to retrieve
 * @param perPage the number of items per page
 */
  public searchForCustomers = (page: number, perPage: number) => {
    this.helpText = undefined;
    this.$rootScope.showSpinner = true;

    const schemeLink = this.linkBuilder.getSchemesLink(this.userService.selectedScheme.id);

    return this.getCustomersByString(this.searchText, schemeLink, page, perPage).then((response) => {
      this.handleGetCustomersByStringResponse(response, schemeLink);
    });
  }

  private handleGetCustomersByStringResponse(
    getCustomerByStringResponse: InterACT.Interfaces.Customers.CustomersResponse,
    schemeLink: string) {
    if (getCustomerByStringResponse.customers.length > 0) {
      this.customers = getCustomerByStringResponse.customers;
      this.customersPaginator.totalCount = getCustomerByStringResponse.meta.totalCount;
      this.$rootScope.showSpinner = false;
    }
    else {
      this.getCustomersByCardNumber(schemeLink);
    }
  }

  private getCustomersByCardNumber(schemeLink: string) {
    this.getCardsByNumber(this.searchText, schemeLink).then((getCardsResponse) => {
      this.handleGetCardsByNumberResponse(<any>getCardsResponse, schemeLink);
    });
  }

  private handleGetCardsByNumberResponse(getCardsResponse: InterACT.Interfaces.Cards.CardsResponse, schemeLink: string) {
    if (getCardsResponse.cards.length > 0) {
      const customerIds = [];

      getCardsResponse.cards.forEach((card) => {
        if (card.customer && card.customer.link) {
          customerIds.push(card.customer.link.split('/').pop());
        }
        else {
          this.anonymousCards.push(card);
        }
      });

      if (customerIds.length > 0) {
        this.customersPaginator.totalCount = customerIds.length;
        this.getCustomersByIds(customerIds, schemeLink).then((response) => {
          this.handleGetCustomersByIdResponse(response);
        });
      }
    } else {
      this.helpText = 'SEARCH_ZERO_RESULTS';
    }
    this.$rootScope.showSpinner = false;
  }

  private handleGetChargeAccountsbyNameOrMediaRefResponse(
    chargeAccountsForCustomerNameOrMediaReResponse: InterACT.Interfaces.ChargeAccounts.ChargeAccountsResponse,
    schemeLink: string) {
    if (chargeAccountsForCustomerNameOrMediaReResponse.chargeAccounts.length === 0) {
      this.getChargeAccountsByPaymentTokenRef(this.searchText, schemeLink).then((chargeAccountsForPaymenToken: Array<InterACT.Interfaces.ChargeAccounts.ChargeAccount>) => {
        this.handleGetChargeAccountsByPaymentTokenResponse(chargeAccountsForPaymenToken, schemeLink);
      });
    } else {
      this.chargeAccounts = chargeAccountsForCustomerNameOrMediaReResponse.chargeAccounts;
      this.chargeAccountsPaginator.totalCount = chargeAccountsForCustomerNameOrMediaReResponse.meta.totalCount;
    }
  }

  private handleGetChargeAccountsByPaymentTokenResponse(chargeAccounts: Array<InterACT.Interfaces.ChargeAccounts.ChargeAccount>, schemeLink: string) {
    if (chargeAccounts.length === 0) {
      this.getChargeAccountsByPaymentReference(this.searchText, schemeLink).then((chargeAccountsForPaymentRef: Array<InterACT.Interfaces.ChargeAccounts.ChargeAccount>) => {
        this.handleGetChargeAccountsByPaymentReferenceResponse(chargeAccountsForPaymentRef);
      });
    } else {
      this.chargeAccounts = chargeAccounts;
      this.chargeAccountsPaginator.totalCount = chargeAccounts.length;
      this.$rootScope.showSpinner = false;
    }
  }

  private handleGetChargeAccountsByPaymentReferenceResponse(chargeAccounts: Array<InterACT.Interfaces.ChargeAccounts.ChargeAccount>) {
    this.chargeAccounts = chargeAccounts;
    this.chargeAccountsPaginator.totalCount = chargeAccounts.length;

    if (this.anonymousCards.length === 0 && this.customers.length === 0 && this.chargeAccounts.length === 0) {
      this.helpText = 'SEARCH_ZERO_RESULTS';
    }
  }

  private handleGetCustomersByIdResponse(response: InterACT.Interfaces.Customers.CustomersResponse) {
    this.customers = response.customers;

    if (this.anonymousCards.length === 0 && this.customers.length === 0 && this.chargeAccounts.length === 0) {
      this.helpText = 'SEARCH_ZERO_RESULTS';
    }
  }

  private getCardsByNumber(isrn: string, schemeLink: string) {
    const cardQuery = this.queryBuilder
      .newParam()
      .fieldIs('scheme.link', schemeLink)
      .and()
      .openSubQuery()
      .fieldIs('cardNumber.ISRN', isrn)
      .or()
      .fieldIs('cardNumber.SERIAL_NUMBER', isrn)
      .closeSubQuery()
      .getParam();

    return this.customerService.getCardList(1, 1, cardQuery);
  }

  private getCustomersByIds(customerIds: Array<string>, schemeLink: string) {
    const customersQuery = this.queryBuilder
      .newParam()
      .fieldIs('scheme.link', schemeLink)
      .and()
      .fieldIn('id', customerIds)
      .getParam();

    return this.customerService.getList(1, 1000, customersQuery);
  }

  private getCustomersByString(searchText: string, schemeLink: string, page: number, perPage: number) {
    const customersQuery = this.queryBuilder
      .newParam()
      .fieldIs('scheme.link', schemeLink)
      .and()
      .not()
      .fieldIs('status', 'INVALID')
      .and()
      .contains(searchText)
      .getParam();

    return this.customerService.getList(page, perPage, customersQuery);
  }

  private getChargeAccountsByCustomerNameOrMediaRef(searchText: string, schemeLink: string, page: number, perPage: number) {
    const chargeAccountsQuery: string = this.queryBuilder
      .newParam()
      .contains(searchText)
      .and()
      .not()
      .fieldIs('status', 'DELETED')
      .and()
      .fieldIs('scheme.link', schemeLink)
      .getParam();

    return this.chargeAccountService.getChargeAccounts(page, perPage, chargeAccountsQuery, true);
  }

  private getChargeAccountsByPaymentTokenRef = (searchText: string, schemeLink: string) => {
    const paymentTokensQuery: string = this.queryBuilder
      .newParam()
      .fieldIs('token', searchText)
      .and()
      .fieldIs('schemes.link', schemeLink)
      .and()
      .not()
      .fieldIs('status', 'DELETED')
      .getParam();

    const deferred = this.$q.defer<Array<InterACT.Interfaces.ChargeAccounts.ChargeAccount>>();

    this.paymentService
      .getPaymentTokens(1, 1, paymentTokensQuery)
      .then((paymentTokens: Array<InterACT.Interfaces.Payments.PaymentToken>) => {
        if (paymentTokens.length === 0) {
          return deferred.resolve([]);
        }

        this.getChargeAccountsByPaymentTokenLink(
          this.linkBuilder.getPaymentTokenLink(paymentTokens[0].id),
          deferred,
          schemeLink
        );
      });

    return deferred.promise;
  }

  private getChargeAccountsByPaymentReference = (searchText: string, schemeLink: string) => {
    const paymentRequestsQuery: string = this.queryBuilder
      .newParam()
      .fieldIs('paymentReference', searchText)
      .and()
      .fieldIs('scheme.link', schemeLink)
      .getParam();

    const deferred = this.$q.defer<Array<InterACT.Interfaces.ChargeAccounts.ChargeAccount>>();

    this.paymentService
      .getPaymentHistory(1, 1, paymentRequestsQuery)
      .then((response: InterACT.Interfaces.Payments.PaymentRequestsResponse) => {
        if (!response.paymentRequests || response.paymentRequests.length === 0) {
          return deferred.resolve([]);
        }

        this.getChargeAccountsByPaymentTokenLink(
          response.paymentRequests[0].paymentToken.link,
          deferred,
          schemeLink
        );
      });

    return deferred.promise;
  }

  private getChargeAccountsByPaymentTokenLink(
    paymentTokenLink: string,
    deferred: ng.IDeferred<Array<InterACT.Interfaces.ChargeAccounts.ChargeAccount>>,
    schemeLink: string) {
    const chargeAccountsQuery: string = this.queryBuilder
      .newParam()
      .fieldIs('paymentToken.link', paymentTokenLink)
      .and()
      .fieldIs('scheme.link', schemeLink)
      .and()
      .not()
      .fieldIs('status', 'DELETED')
      .getParam();

    this.chargeAccountService
      .getChargeAccounts(1, 1, chargeAccountsQuery)
      .then((chargeAccounts: Array<InterACT.Interfaces.ChargeAccounts.ChargeAccount>) => {
        return deferred.resolve(chargeAccounts);
      });
  }

  private activate() {
    this.shouldSearchChargeAccounts =
      this.userService.permissions.ui &&
      this.userService.permissions.ui['charge-account'] &&
      this.userService.permissions.ui['charge-account'].canAccess;

    this.canEditCustomers =
      this.userService.permissions.ui &&
      this.userService.permissions.ui['customers'] &&
      this.userService.permissions.ui['customers'].canUpdate;

    this.breadcrumbService.setBreadcrumbs([
      {
        name: 'ACCOUNT_SEARCH',
        sref: 'customers'
      }
    ]);

    this.toolbarService.getToolbar('primary').then((toolbar: any) => {
      toolbar.setTitle('ACCOUNT_SEARCH');
      if (this.userService.permissions.ui.customers.canCreate) {
        this.PrimaryAction.setActions([{
          name: 'Create customer',
          icon: 'person_add',
          action: this.goToCreate
        }]);
      }
    });
  }

   /**
   * A method that calculates the total owed on the charge account
   *
   * @param chargeAccount An object representation of a charge account
   *
   * @returns A calculated value for the total owed on the charge account
   */
  public getTotalOwed(chargeAccount: InterACT.Interfaces.ChargeAccounts.ChargeAccount) {
    return chargeAccount.unsettledCharges.amount + chargeAccount.outstandingDebt.amount - chargeAccount.unsettledRefunds;
  }
}
